import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { doc, setDoc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/services/firebase/config'
import { useAuthStore, isAdmin } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { POST_AUTH_RETURN_URL } from '@/utils/constants'
import type { UserProfile, CreateUserData } from '@/types/user'

// Flag to prevent safety-net doc creation from racing with signup flow
let signupInProgress = false

// Hook to initialize auth state listener (call once in App)
export function useAuthInit() {
  const { setUser, setProfile, setLoading } = useAuthStore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)

      if (firebaseUser) {
        // Load user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          if (userDoc.exists()) {
            const profileData = userDoc.data() as UserProfile
            setProfile(profileData)

            // Sync Firestore email if Auth email changed (e.g. after verifyBeforeUpdateEmail)
            if (firebaseUser.email && profileData.email !== firebaseUser.email) {
              const { updateDoc, serverTimestamp } = await import('firebase/firestore')
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                email: firebaseUser.email,
                updatedAt: serverTimestamp(),
              })
              setProfile({ ...profileData, email: firebaseUser.email })
            }
          } else if (firebaseUser.email && !signupInProgress) {
            // Safety net: Auth account exists but Firestore document is missing
            // (can happen if signup Firestore write failed due to network issues).
            // Skipped during active signup to avoid racing with the signup flow.
            //
            // CRITICAL: write create-only inside a transaction. The signup flow may be
            // writing the real profile (possibly a higher tier — nonprofit/storefront)
            // concurrently; a plain setDoc here can land AFTER it and clobber the tier
            // back to 'general', silently downgrading e.g. a school/fundraiser account.
            // The transaction re-reads inside the commit so we NEVER overwrite an
            // existing doc.
            const fallbackProfile: Record<string, unknown> = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || 'User',
              location: '',
              accountType: 'buyer',
              subscriptionTier: 'general',
              role: 'user',
              verified: false,
              listingCount: 0,
              salesCount: 0,
              rating: 0,
              reviewCount: 0,
              banned: false,
              showEmail: false,
              showPhone: false,
              enableChat: true,
              enableEnquiryForm: false,
              bio: '',
              phone: '',
              monthlyListingCount: 0,
              createdAt: serverTimestamp(),
            }
            const userRef = doc(db, 'users', firebaseUser.uid)
            const created = await runTransaction(db, async (tx) => {
              const snap = await tx.get(userRef)
              if (snap.exists()) return false // signup flow won the race — don't touch it
              tx.set(userRef, fallbackProfile)
              return true
            })
            if (created) {
              setProfile(fallbackProfile as unknown as UserProfile)
              console.warn('Created missing Firestore profile for user:', firebaseUser.uid)
            } else {
              // The real profile was written by the signup flow in the meantime —
              // load it rather than the general-tier fallback.
              const fresh = await getDoc(userRef)
              if (fresh.exists()) setProfile(fresh.data() as UserProfile)
            }
          }
        } catch (error) {
          console.error('Error loading user profile:', error)
        }
      } else {
        setProfile(null)
        // Guest checkout: with no signed-in user, sign in anonymously so the
        // cart and checkout work without an account. The next onAuthStateChanged
        // fires with the anonymous user; we finish loading on that pass.
        try {
          await signInAnonymously(auth)
          return
        } catch (err) {
          console.error('Anonymous sign-in failed:', err)
        }
      }

      setLoading(false)
    })

    return unsubscribe
  }, [setUser, setProfile, setLoading])
}

// Hook for auth actions
export function useAuth() {
  const { user, profile, isLoading, isAuthenticated, logout: clearAuth } = useAuthStore()
  const { showToast, closeModal } = useUIStore()
  const navigate = useNavigate()

  const login = async (email: string, password: string) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      closeModal()
      showToast('Welcome back!')
      // Admin accounts land on /admin after login — matches the legacy-site
      // behaviour the client team expects. The tier check needs the Firestore
      // profile, which may not be in the store yet, so read it directly.
      try {
        const snap = await getDoc(doc(db, 'users', cred.user.uid))
        const prof = snap.exists() ? (snap.data() as UserProfile) : null
        if (isAdmin(cred.user.email, prof)) {
          navigate('/admin')
        }
      } catch {
        // Non-fatal — stay on the current page if the profile read fails
      }
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string }
      const messages: Record<string, string> = {
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/invalid-email': 'Please enter a valid email address',
        'auth/invalid-credential': 'Invalid email or password',
      }
      showToast(messages[firebaseError.code || ''] || 'Login failed. Please try again.', 'error')
      throw error
    }
  }

  const signup = async (
    data: Omit<CreateUserData, 'subscriptionTier'> & { password: string },
    tier?: 'general' | 'nonprofit',
    nonprofitInfo?: { charityNumber?: string; schoolAddress?: string },
    businessInfo?: { gstNumber?: string },
  ) => {
    try {
      // Prevent safety-net from racing with signup
      signupInProgress = true

      // Create Firebase auth user
      const { user: newUser } = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      )

      // Update display name
      await updateProfile(newUser, { displayName: data.displayName })

      // Create user profile in Firestore
      // Nonprofit gets tier immediately (pending admin verification)
      // Paid tiers (storefront/dealership) start as general, activated via Stripe webhook
      const initialTier = tier === 'nonprofit' ? 'nonprofit' : 'general'

      const userProfile: Record<string, unknown> = {
        uid: newUser.uid,
        email: data.email,
        displayName: data.displayName,
        location: data.location,
        accountType: data.accountType,
        subscriptionTier: initialTier,
        role: 'user',
        verified: false,
        listingCount: 0,
        salesCount: 0,
        rating: 0,
        reviewCount: 0,
        createdAt: serverTimestamp(),
      }

      // Store nonprofit verification details for admin review
      if (initialTier === 'nonprofit' && nonprofitInfo) {
        if (nonprofitInfo.charityNumber) userProfile.charityNumber = nonprofitInfo.charityNumber
        if (nonprofitInfo.schoolAddress) userProfile.schoolAddress = nonprofitInfo.schoolAddress
        userProfile.nonprofitVerified = false // pending admin verification
      }

      // GST-registered business tiers: store the GST number so it shows on the
      // tax invoices their buyers receive from day one (opt-out at signup).
      if (businessInfo?.gstNumber) userProfile.gstNumber = businessInfo.gstNumber

      await setDoc(doc(db, 'users', newUser.uid), userProfile)
      signupInProgress = false

      // Fire the verification email immediately — users expect one in their
      // inbox right after sign-up. Best-effort: a failure here (rate limit,
      // network blip) must not undo the account; the EmailVerificationBanner
      // exposes a manual Resend as a fallback.
      try {
        await sendEmailVerification(newUser, {
          url: POST_AUTH_RETURN_URL,
          handleCodeInApp: false,
        })
        showToast('Welcome to iSQROLL! Check your inbox to verify your email.')
      } catch (err) {
        console.error('Verification email failed to send on signup:', err)
        showToast('Welcome to iSQROLL! You can verify your email from the banner at the top.')
      }
    } catch (error: unknown) {
      signupInProgress = false
      const firebaseError = error as { code?: string; message?: string }
      const messages: Record<string, string> = {
        'auth/email-already-in-use': 'An account with this email already exists',
        'auth/weak-password': 'Password must be at least 6 characters',
        'auth/invalid-email': 'Please enter a valid email address',
      }
      showToast(messages[firebaseError.code || ''] || 'Signup failed. Please try again.', 'error')
      throw error
    }
  }

  const logout = async () => {
    await signOut(auth)
    clearAuth()
    showToast('Logged out successfully')
  }

  const sendVerification = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser, {
          url: POST_AUTH_RETURN_URL,
          handleCodeInApp: false,
        })
        showToast('Verification email sent! Check your inbox (and spam folder).')
      } catch {
        showToast('Could not send verification email. Try again later.', 'error')
      }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      // continueUrl is hardcoded to the canonical app URL (not window.location.origin)
      // so Firebase Auth always sees an authorized domain regardless of which
      // staging/dev/preview host the request originated from.
      await sendPasswordResetEmail(auth, email, {
        url: POST_AUTH_RETURN_URL,
        handleCodeInApp: false,
      })
      showToast('Password reset email sent! Check your inbox (and spam folder).')
    } catch (error: unknown) {
      const firebaseError = error as { code?: string }
      if (firebaseError.code === 'auth/user-not-found') {
        showToast('No account found with this email', 'error')
      } else if (firebaseError.code === 'auth/invalid-email') {
        showToast('That email address looks invalid. Please check and try again.', 'error')
      } else if (firebaseError.code === 'auth/too-many-requests') {
        showToast('Too many attempts. Please wait a few minutes and try again.', 'error')
      } else {
        showToast('Could not send reset email. Try again later.', 'error')
      }
      throw error
    }
  }

  return {
    user,
    profile,
    isLoading,
    isAuthenticated,
    emailVerified: user?.emailVerified ?? false,
    login,
    signup,
    logout,
    sendVerification,
    resetPassword,
  }
}
